import { User } from '@auth0/auth0-react';
import { axiosPost } from '@gr/portal/authAxios';
import { useAuth0Context } from '@gr/portal/contexts/Auth0ConfigContext';
import { NotificationType, useNotifications } from '@gr/portal/contexts/NotificationContext';
import useAssignedClients from '@gr/portal/hooks/useAssignedClients';
import { useAuth0UserData } from '@gr/portal/hooks/useAuth0Custom';
import useClickerGroups from '@gr/portal/hooks/useClickerGroups';
import useRoles from '@gr/portal/hooks/useRoles';
import { getUserDetails } from '@gr/portal/providers/auth0.service';
import { getApiErrorMessage, getReadableRoleName } from '@gr/portal/providers/utility.provider';
import { Auth0GetUserDetailsRequest, Auth0OrganizationName, Auth0Role, Auth0UserDetails } from '@gr/shared/models';
import { areRolesEqual, getEquivalentRole } from '@gr/shared/utils';
import { Button, ButtonVariantEnum, LogoLoadingIndicator, RightArrowIcon, Static } from '@Wonder-Cave/ui';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

function getOrganization(user?: Auth0UserDetails, auth0User?: User) {
  const organization: Auth0OrganizationName = auth0User?.organization_name;
  return user ? getUserOrganization(user, organization) : organization;
}

function getUserOrganization(user: Auth0UserDetails, organization: Auth0OrganizationName) {
  const memberships = user?.app_metadata?.memberships ?? [];

  const match = memberships.find((m) => m?.organization === organization)?.organization;
  const first = memberships?.[0]?.organization;

  return match ?? first;
}

const ViewUser = () => {
  const { id } = useParams<any>();
  const [user, setUser] = useState<Auth0UserDetails>();
  const { user: auth0User } = useAuth0UserData();
  const history = useHistory();
  const [resendLoading, setResendLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [resetMfaLoading, setResetMfaLoading] = useState(false);
  const { addNotification } = useNotifications();
  const { auth0Config } = useAuth0Context();
  const resetMfaAllowed = useRoles([Auth0Role.GR_STAFF_MANAGER]);
  const isSuperAdmin = useRoles([Auth0Role.GR_ADMIN]);

  const { data: clickerGroups } = useClickerGroups();
  const [{ data: assignedClientData, loading: clientsLoading, error }, refetchAssignedClients] = useAssignedClients({
    userId: id,
  });

  const role = getEquivalentRole(user?.app_metadata?.memberships?.[0].roles?.[0]);
  const isTargetAdmin = !!user?.app_metadata?.memberships?.flatMap((m) => m.roles).some((r) => areRolesEqual(Auth0Role.GR_ADMIN, r));

  const getUser = async (id: string) => {
    const request: Auth0GetUserDetailsRequest = { user_id: id ?? '' };
    const user = await getUserDetails(request);
    await refetchAssignedClients({
      params: {
        userId: id,
      },
    });
    setUser(user);
  };

  useEffect(() => {
    getUser(id);
  }, [id]);

  const resendEmail = async () => {
    try {
      setResendLoading(true);
      await axiosPost('/auth0-send-user-welcome-email', { user_id: id });
      addNotification({
        content: 'Email successfully sent.',
        type: NotificationType.SUCCESS,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setResendLoading(false);
    }
  };

  const removeUserFromOrganization = async () => {
    try {
      setRemoveLoading(true);
      await axiosPost('/auth0-remove-user-membership', { user_id: id, organization: auth0Config?.organization });
      addNotification({
        content: 'User successfully removed.',
        type: NotificationType.SUCCESS,
      });
      history.push('/app/users');
    } catch (e) {
      console.error(e);
      addNotification({
        content: getApiErrorMessage(e, 'Unable to remove this user. You may not be authorized to remove this user.'),
        type: NotificationType.FAILURE,
      });
    } finally {
      setRemoveLoading(false);
    }
  };

  const resetMfa = async () => {
    try {
      setResetMfaLoading(true);
      await axiosPost('/auth0-reset-mfa', { user_id: id });
      addNotification({
        content: 'Reset MFA Email successfully sent.',
        type: NotificationType.SUCCESS,
      });
    } catch (e) {
      console.error(e);
      addNotification({
        content: 'Unable to reset MFA for this user. You may not be authorized to reset MFA for this user.',
        type: NotificationType.FAILURE,
      });
    } finally {
      setResetMfaLoading(false);
    }
  };

  return <div>
    {user ? <div className='px-24 pt-8'>
      <div className='flex justify-between'>
        <h1>{user?.given_name + ' ' + user?.family_name}</h1>
        <div className='flex flex-wrap justify-end gap-4'>
          <Button isLoading={removeLoading} onClick={removeUserFromOrganization} variant={ButtonVariantEnum.DELETE}>DELETE</Button>
          {resetMfaAllowed && (isSuperAdmin || !isTargetAdmin) && <Button isLoading={resetMfaLoading} onClick={resetMfa} variant={ButtonVariantEnum.SECONDARY}>RESET MFA</Button>}
          <Button variant={ButtonVariantEnum.TERTIARY} onClick={() => history.push(`/app/users/${id}/edit`)}>EDIT USER</Button>
        </div>
      </div>
      <div className='flex pt-16'>
        <Static className='basis-1/5 grow-0 shrink-0' title='FIRST NAME'>{user?.given_name}</Static>
        <Static className='basis-1/5 grow-0 shrink-0' title='LAST NAME'>{user?.family_name}</Static>
        <Static className='basis-2/5 grow-0 shrink-0' title='EMAIL'>{user?.email}</Static>
      </div>
      <div className='flex pt-8'>
        <div className='basis-1/5 grow-0 shrink-0'>
          <Static className='basis-1/5 grow-0 shrink-0' title='ROLE'>{role ? getReadableRoleName(role) : 'NONE'}</Static>
          <Button
            className='mt-16'
            trailingIcon={<RightArrowIcon />}
            variant={ButtonVariantEnum.GREEN}
            onClick={resendEmail}
            type="button"
            isLoading={resendLoading}
          >Resend Email</Button>
        </div>
        <Static className='basis-2/5 grow-0 shrink-0' title='AGENT GROUP'>{clickerGroups?.find(cg => cg.id === user?.clickerGroupId)?.name ?? '-'}</Static>
        <Static className='basis-1/5 grow-0 shrink-0' title=''></Static>
        <Static className='basis-1/5 grow-0 shrink-0' title='CLIENTS'>{areRolesEqual(Auth0Role.GR_ADMIN, role) ? 'All Clients' : assignedClientData?.data?.map((client, index) => { return <div>{index + 1}. {client.name}</div>; })}</Static>
      </div>
    </div> : <div className='flex items-center justify-center pt-[22%]'><LogoLoadingIndicator /></div>}
  </div>;

};

export default ViewUser;